import * as z from 'zod';

import { emailQuerySchema } from '@/_containers/inputs/emails/shared/schema';
import { contactQueryLinkerSchema } from '@/_pages/contacts/shared/schemas/contact-query-linker-schema';
import { paginatedResponseSchema } from '@/_schemas/generic/paginated-response';
import { teamMemberQueryLinkerSchema } from '@/_schemas/team-members/team-members-query-linker-schema';

// Support ticket enum objects
export const SupportTicketPriority = {
  LOW: 1,
  MEDIUM: 2,
  HIGH: 3,
  URGENT: 4,
} as const;

export const SupportTicketStatus = {
  OPEN: 2,
  PENDING: 3,
  RESOLVED: 4,
  CLOSED: 5,
  WAITING_ON_CUSTOMER: 6,
  WAITING_ON_THIRD_PARTY: 7,
  WAITING_ON_DEVELOPER: 8,
} as const;

export const SupportTicketSource = {
  EMAIL: 1,
  PORTAL: 2,
  PHONE: 3,
  CHAT: 7,
  FEEDBACK_WIDGET: 9,
  OUTBOUND_EMAIL: 10,
} as const;

// Zod schemas using enum values
export const supportPrioritySchema = z.enum(SupportTicketPriority);
export type SupportPriority = z.infer<typeof supportPrioritySchema>;
export const supportStatusSchema = z.enum(SupportTicketStatus);
export type SupportStatus = z.infer<typeof supportStatusSchema>;
export const supportSourceSchema = z.enum(SupportTicketSource);
export type SupportSource = z.infer<typeof supportSourceSchema>;

// Display label mappings
export const priorityLabels: Record<SupportPriority, string> = {
  [SupportTicketPriority.LOW]: 'Low',
  [SupportTicketPriority.MEDIUM]: 'Medium',
  [SupportTicketPriority.HIGH]: 'High',
  [SupportTicketPriority.URGENT]: 'Urgent',
};

export const statusLabels: Record<SupportStatus, string> = {
  [SupportTicketStatus.OPEN]: 'Open',
  [SupportTicketStatus.PENDING]: 'Pending',
  [SupportTicketStatus.RESOLVED]: 'Resolved',
  [SupportTicketStatus.CLOSED]: 'Closed',
  [SupportTicketStatus.WAITING_ON_CUSTOMER]: 'Waiting on Customer',
  [SupportTicketStatus.WAITING_ON_THIRD_PARTY]: 'Waiting on Third Party',
  [SupportTicketStatus.WAITING_ON_DEVELOPER]: 'Waiting on Developer',
};

export const sourceLabels: Record<SupportSource, string> = {
  [SupportTicketSource.EMAIL]: 'Email',
  [SupportTicketSource.PORTAL]: 'Portal',
  [SupportTicketSource.PHONE]: 'Phone',
  [SupportTicketSource.CHAT]: 'Chat',
  [SupportTicketSource.FEEDBACK_WIDGET]: 'Feedback Widget',
  [SupportTicketSource.OUTBOUND_EMAIL]: 'Outbound Email',
};

// Nested schemas for support ticket entities
export const supportRequesterSchema = contactQueryLinkerSchema.extend({
  emails: z.array(emailQuerySchema),
});

export const supportAssigneeSchema = z.object({
  id: z.number(),
  name: z.string(),
  email: z.string(),
  avatar: z.string().nullable(),
  signature: z.string().nullable(),
});

export const supportCompanySchema = z.object({
  id: z.number(),
  name: z.string(),
});

export const supportSlaInfoSchema = z.object({
  policy_name: z.string(),
  escalated: z.boolean(),
});

export const supportAttachmentSchema = z.object({
  id: z.number(),
  name: z.string(),
  content_type: z.string(),
  size: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
  attachment_url: z.string(),
});

export const agentQuerySchema = teamMemberQueryLinkerSchema;
//  z.object({
//   contact: z
//     .object({
//       id: z.number(),
//       email: z.string().email(),
//       phone: z.string().nullable(),
//       mobile: z.string().nullable(),
//       name: z.string().nullable(),
//       job_title: z.string().nullable(),
//       avatar: z
//         .object({
//           attachment_url: z.string().nullable(),
//           thumb_url: z.string().nullable(),
//         })
//         .nullish(),
//     })
//     .nullish(),
// });

export type AgentQuerySchema = z.infer<typeof agentQuerySchema>;

// A conversation's user can be either a simple user shape or an agent with contact info
export const conversationUserSchema = z.union([supportRequesterSchema, agentQuerySchema]);

export type ConversationUser = z.infer<typeof conversationUserSchema>;

export const supportConversationSchema = z.object({
  id: z.number(),
  body: z.string(),
  body_text: z.string(),
  from_email: z.string(),
  to_emails: z.array(z.string()),
  cc_emails: z.array(z.string()),
  bcc_emails: z.array(z.string()),
  private: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
  user_id: z.number().nullable(),
  user_name: z.string().nullable(),
  user_avatar: z.string().nullable(),
  support_email: z.string().nullable(),
  source: supportSourceSchema,
  incoming: z.boolean(),
  attachments: z.array(supportAttachmentSchema),
  user: conversationUserSchema,
  user_type: z.enum(['unknown', 'agent', 'contact']),
});

// Support ticket summary schema (for list view)
export const supportTicketSummarySchema = z.object({
  id: z.number(),
  subject: z.string(),
  description: z.string(),
  description_text: z.string(),
  status: supportStatusSchema,
  priority: supportPrioritySchema,
  source: supportSourceSchema,
  requester_id: z.number(),
  requester: supportRequesterSchema.nullable(),
  company: supportCompanySchema.nullable(),
  responder_id: z.number().nullable(),
  company_id: z.number().nullable(),
  product_id: z.number().nullable(),
  group_id: z.number().nullable(),
  due_by: z.string().nullable(),
  fr_due_by: z.string().nullable(),
  is_escalated: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
  tags: z.array(z.string()),
  spam: z.boolean(),
  type: z.string().nullable(),
  custom_fields: z.record(z.string(), z.unknown()).nullable(),

  agent: agentQuerySchema.nullish(),
});

// Support ticket detail schema (for detail view)
export const supportTicketDetailSchema = supportTicketSummarySchema.extend({
  bcc_emails: z.array(z.email()).nullable(),
  to_emails: z.array(z.email()).nullable(),
  cc_emails: z.array(z.email()).nullable(),
  conversations: z.array(supportConversationSchema),
  sla_policy: supportSlaInfoSchema.nullable(),
  escalated_at: z.string().nullable(),
  attachments: z.array(supportAttachmentSchema),
});

// Query schema for filtering and pagination
export const supportTicketQuerySchema = z.object({
  companyId: z.string().optional(),
  contactId: z.string().optional(),
  page: z.number().prefault(1),
  per_page: z.number().prefault(25),
  order_by: z.string().optional().prefault('priority'),
  order_type: z.literal('asc').or(z.literal('desc')).optional().prefault('desc'),
  include: z.string().optional(),
});

// Paginated response schema
export const supportTicketListResponseSchema = paginatedResponseSchema(supportTicketSummarySchema);

// Export types
export type SupportTicketSummary = z.infer<typeof supportTicketSummarySchema>;

export type SupportTicketDetailSchema = z.infer<typeof supportTicketDetailSchema>;

export type SupportTicketQuery = z.infer<typeof supportTicketQuerySchema>;
export type SupportTicketListResponse = z.infer<typeof supportTicketListResponseSchema>;
export type SupportConversation = z.infer<typeof supportConversationSchema>;
export type SupportRequester = z.infer<typeof supportRequesterSchema>;
export type SupportAssignee = z.infer<typeof supportAssigneeSchema>;
export type SupportSlaInfo = z.infer<typeof supportSlaInfoSchema>;
export type SupportAttachment = z.infer<typeof supportAttachmentSchema>;

// Options for select inputs
export const priorityOptions = Object.entries(priorityLabels).map(([value, label]) => ({
  value,
  label,
}));

export const supportStatusOptions = Object.entries(statusLabels).map(([value, label]) => ({
  value,
  label,
}));

export const supportModuleOptions = z.enum(['all', 'companies', 'contacts']);

export type SupportModule = z.infer<typeof supportModuleOptions>;
